import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="mb-12">
          <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-4 font-heading">
            <span className="text-lime-400">Premium</span> Briquettes
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-300 max-w-3xl">
            Choose from our range of 90mm biomass briquettes. All products include same-day delivery across Bengaluru.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors group relative overflow-hidden"
                data-testid={`product-card-${product.id}`}
              >
                {product.badge && (
                  <div className="absolute top-4 right-4 bg-orange-500 text-white px-2 py-1 text-xs font-bold uppercase tracking-widest">
                    {product.badge}
                  </div>
                )}
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-56 object-cover mb-4 border border-zinc-800"
                />
                <h3 className="text-2xl font-semibold tracking-tight font-heading uppercase mb-2">
                  {product.name}
                </h3>
                <p className="text-zinc-400 text-sm mb-4">{product.description}</p>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-3xl font-bold text-lime-400">₹{product.price}</span>
                  {product.original_price && (
                    <span className="text-zinc-500 line-through">₹{product.original_price}</span>
                  )}
                </div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {product.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-lime-400/10 text-lime-400 border border-lime-400/20 px-2 py-1 text-xs font-mono uppercase"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-6 py-3 transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Add to Cart
                  </button>
                  <Link
                    to={`/products/${product.id}`}
                    className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-6 py-3 transition-colors flex items-center justify-center"
                    data-testid={`view-details-${product.id}`}
                  >
                    Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
